import React, { useRef, useCallback, useEffect } from 'react'
import { Capacitor } from '@capacitor/core'
import { isCapacitor } from '../services/googleAuthService'

/**
 * useImagePicker — unified image/video picker for web and Capacitor native.
 *
 * On web:      renders a hidden <input type="file"> and triggers it on openPicker().
 * On Capacitor: uses @capacitor/camera native OS picker — no DOM input rendered.
 *
 * @param {object}   options
 * @param {string}   options.accept    - MIME types for web input (default: 'image/*')
 * @param {boolean}  options.multiple  - Allow multi-select (default: false)
 * @param {function} options.onFiles   - Called with File[] when the user picks files
 *
 * @returns {{ openPicker: function, pickerInput: JSX|null }}
 */
export function useImagePicker({ accept = 'image/*', multiple = false, onFiles }) {
  const inputRef = useRef(null)

  // Pre-warm the camera module so the first openPicker() call is instant.
  // Without this, the first dynamic import takes noticeable time on device.
  useEffect(() => {
    if (isCapacitor) {
      import('@capacitor/camera').catch(() => {})
    }
  }, [])

  const openPicker = useCallback(async () => {
    if (isCapacitor) {
      try {
        const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera')

        // CapacitorHttp dataType:'file' decodes a base64 string into binary (CapacitorUrlRequest.swift:179).
        // It does NOT read from a file path — the data field must be a pure base64 string.

        // Convert Blob → pure base64 string (no data URL prefix).
        const blobToBase64 = (blob) => new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result.split(',')[1])
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })

        // pickImages always returns JPEG on iOS/Android per Capacitor docs.
        // convertFileSrc makes the native path web-accessible so fetch() can read it.
        const makeFileFromGalleryPhoto = async (photo) => {
          const url = Capacitor.convertFileSrc(photo.webPath)
          const blob = await fetch(url).then(r => r.blob())
          const file = new File([blob], 'photo.jpeg', { type: 'image/jpeg' })
          file._base64Data = await blobToBase64(blob)
          return file
        }

        // getPhoto with CameraResultType.Base64 forces HEIC→JPEG conversion before returning.
        // Decode base64 → Blob so the crop modal can render the preview from file content.
        const makeFileFromBase64Photo = (photo) => {
          const base64 = photo.base64String
          const binaryString = atob(base64)
          const bytes = new Uint8Array(binaryString.length)
          for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i)
          const blob = new Blob([bytes], { type: 'image/jpeg' })
          const file = new File([blob], 'photo.jpeg', { type: 'image/jpeg' })
          file._base64Data = base64
          return file
        }

        if (multiple) {
          const result = await Camera.pickImages({ quality: 90 })
          const files = await Promise.all(result.photos.map(makeFileFromGalleryPhoto))
          onFiles(files)
        } else {
          const photo = await Camera.getPhoto({
            resultType: CameraResultType.Base64,
            source: CameraSource.Prompt,
            quality: 90,
          })
          onFiles([makeFileFromBase64Photo(photo)])
        }
      } catch (err) {
        // User dismissed the picker — not an error worth logging
        if (err?.message !== 'User cancelled photos app') {
          console.error('[useImagePicker]', err)
        }
      }
    } else {
      inputRef.current?.click()
    }
  }, [multiple, onFiles])

  const handleChange = useCallback((e) => {
    const files = Array.from(e.target.files || [])
    if (files.length) onFiles(files)
    e.target.value = ''
  }, [onFiles])

  const pickerInput = isCapacitor ? null : (
    <input
      ref={inputRef}
      type="file"
      accept={accept}
      multiple={multiple}
      className="hidden"
      onChange={handleChange}
    />
  )

  return { openPicker, pickerInput }
}
